import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcedureAccessLevel} from 'procedures/design/validation/determineProcedureAccessLevel';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';

export interface GetBannerForReadOnlyProcedureParams {
  procedureType: ProcedureType;
  fullyBlockedOnServerError: boolean;
  isLatestFlowchartVersion: boolean;
  isViewingPublishedVersion: boolean;
  isProcedureArchived: boolean;
  isWritable: boolean;
  hasAccessToEditConfig: boolean;
  accessLevel: ProcedureAccessLevel | null;
}

/**
 * There are many reasons for a procedure to be read-only. These messages are shown in a banner to explain.
 * Returns null if the procedure is open for edits.
 */
export default function getBannerForReadOnlyProcedure(
  params: GetBannerForReadOnlyProcedureParams
): string | null {
  const {
    procedureType,
    fullyBlockedOnServerError,
    isLatestFlowchartVersion,
    isViewingPublishedVersion,
    isProcedureArchived,
    isWritable,
    hasAccessToEditConfig,
    accessLevel,
  } = params;
  const procedureLowerUITitle = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const baseMessage = 'Editing is disabled';

  if (fullyBlockedOnServerError) {
    return `${baseMessage}: a server error occurred.`;
  } else if (!isLatestFlowchartVersion) {
    return `${baseMessage} on an old ${procedureLowerUITitle} version.`;
  } else if (isViewingPublishedVersion) {
    return `${baseMessage} on an effective ${procedureLowerUITitle}. Create a new draft to edit this ${procedureLowerUITitle}.`;
  } else if (isProcedureArchived) {
    return `${baseMessage} on archived ${procedureLowerUITitle}.`;
  } else if (!isWritable || !hasAccessToEditConfig) {
    return `You do not have permission to edit this ${procedureLowerUITitle}.`;
  } else if (accessLevel === ProcedureAccessLevel.READ_ONLY_HIDE_DETAILS) {
    return `${baseMessage}: you may not have access to all elements of this ${procedureLowerUITitle}.`;
  }
  return null;
}
